import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Header, Footer, RTLProvider, LanguageSelector } from "@olorin/shared";
import { useTranslation } from "react-i18next";

const HomePage = lazy(() => import("./pages/HomePage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const SolutionsPage = lazy(() => import("./pages/SolutionsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const LoadingFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center font-mono"
    style={{ backgroundColor: "var(--crt-bg, #000000)" }}
  >
    <span
      style={{
        color: "var(--crt-purple, #FFB000)",
        animation: "blink-cursor 1.06s step-end infinite",
      }}
    >
      LOADING_
    </span>
  </div>
);

function App() {
  const { t } = useTranslation();
  const navigation = [
    { name: t("nav.home"), href: "/" },
    { name: t("nav.features"), href: "/features" },
    { name: t("nav.solutions"), href: "/solutions" },
    { name: t("nav.demo"), href: "/demo" },
    { name: t("nav.download"), href: "/download" },
  ];

  const dropdowns = [
    {
      label: String(t("nav.resourcesDropdown")),
      items: [
        {
          name: t("nav.pricing"),
          href: "/pricing",
          description: String(t("nav.pricingDescription")),
        },
        {
          name: t("nav.demo"),
          href: "/demo",
          description: String(t("nav.demoDescription")),
        },
        {
          name: t("nav.contact"),
          href: "/contact",
          description: String(t("nav.contactDescription")),
        },
        {
          name: t("nav.privacy"),
          href: "/privacy",
          description: String(t("nav.privacyDescription")),
        },
      ],
    },
  ];

  return (
    <RTLProvider>
      <Router>
        <div
          className="App min-h-screen"
          style={{ backgroundColor: "var(--crt-bg)" }}
        >
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-6 focus:py-3"
            style={{
              backgroundColor: "var(--crt-purple)",
              color: "var(--crt-bg)",
            }}
            tabIndex={0}
          >
            {t("a11y.skipToContent")}
          </a>
          <Header
            domain="cvplus"
            navigation={navigation}
            dropdowns={dropdowns}
            showDemo={false}
            ctaText={String(t("nav.getStarted"))}
            ctaHref="https://app.cvplus.ai"
            LanguageSelectorComponent={LanguageSelector}
          />
          <main
            id="main-content"
            role="main"
            aria-label={String(t("a11y.mainContent"))}
          >
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/solutions" element={<SolutionsPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/demo" element={<DemoPage />} />
                <Route path="/download" element={<DownloadPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route
                  path="/checkout/success"
                  element={<CheckoutSuccessPage />}
                />
                <Route path="/account/delete" element={<DeleteAccountPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer
            domain="cvplus"
            companyDescription={String(t("footer.companyDescription"))}
            quickLinks={[
              { name: String(t("nav.home")), href: "/" },
              { name: String(t("nav.features")), href: "/features" },
              { name: String(t("nav.pricing")), href: "/pricing" },
              { name: String(t("nav.contact")), href: "/contact" },
            ]}
          />
        </div>
      </Router>
    </RTLProvider>
  );
}

export default App;
